import {
    AdvancedDynamicTexture,
    Button,
    Container,
    Control,
    Image,
    RadioButton,
    Rectangle,
    StackPanel,
    TextBlock,
} from "@babylonjs/gui"
import { CCUtils } from "../../../common/CCUtils"
import { log } from "../../../common/Logging"
import { FontType } from "../../../common/TextUtils"
import { TimeUtils } from "../../../common/TimeUtils"
import { BabTimer } from "../../BabUtils"
import { MazeAudio } from "./assets/MazeAudio"
import { mazeImages } from "./assets/MazeImages"
import { CameraType } from "./Cameras"
import { MazeCigs } from "./MazeCigs"
import { MazeStore } from "./MazeStore"
import { SimpleCameraInput } from "./SimpleCameraInput"

export class Gui {
    static readonly windowScaling = window.innerHeight / 800
    static readonly padding = 24 * Gui.windowScaling
    private static readonly buttonSide = 60 * Gui.windowScaling
    private static readonly buttonSpacing = 10 * Gui.windowScaling
    private static readonly buttonWidthSpace =
        Gui.buttonSide + Gui.buttonSpacing
    private static readonly percentWidth = 0.2
    private static readonly color = "white"
    private static readonly titleFontSize = 38

    onTimerComplete?: () => void
    timerSeconds: number

    private readonly gui: AdvancedDynamicTexture
    private moveButtons: Array<Button> = []
    private debugButtons: Array<Button> = []
    private showMoveButtons = false
    private guiPanel: Rectangle
    private readonly guiLeft: number
    private timerTitle: TextBlock
    private timerText: TextBlock
    private textBlock: TextBlock
    private timerInterval: NodeJS.Timeout
    private moveInterval?: NodeJS.Timeout
    private smokerTimerInterval?: NodeJS.Timeout
    private timerImages: Array<Image> = []
    private timerCig?: Image
    private teenTitle: TextBlock
    private pawnImage?: Image
    private pawnText?: TextBlock
    private currentBulletText = ""

    constructor() {
        const { cameras, width, stages } = MazeStore.instance

        if (cameras.currentCamera === CameraType.FREE) {
            this.showMoveButtons = true
        }

        this.guiLeft = width * (1 - Gui.percentWidth) + Gui.padding
        log.debug("Setup gui.")
        this.gui = AdvancedDynamicTexture.CreateFullscreenUI("myUI")

        const guiPanel = new Rectangle()
        this.guiPanel = guiPanel
        guiPanel.height = 1
        guiPanel.width = Gui.percentWidth
        guiPanel.background = "black"
        guiPanel.alpha = 0.7
        guiPanel.thickness = 0
        guiPanel.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_RIGHT
        this.gui.addControl(guiPanel)

        const guiMoveMultiplier = 3 * SimpleCameraInput.MOVE_SPEED_MULTIPLIER

        this.addMoveButton(
            "left",
            -Gui.buttonWidthSpace,
            -Gui.buttonWidthSpace,
            () => {
                SimpleCameraInput.turn(
                    cameras.freeCamera,
                    true,
                    guiMoveMultiplier,
                )
            },
        )
        this.addMoveButton("forward", 0, -Gui.buttonWidthSpace * 2, () => {
            SimpleCameraInput.move(cameras.freeCamera, true, guiMoveMultiplier)
        })
        this.addMoveButton("back", 0, -Gui.buttonWidthSpace, () => {
            SimpleCameraInput.move(cameras.freeCamera, false, guiMoveMultiplier)
        })
        this.addMoveButton(
            "right",
            Gui.buttonWidthSpace,
            -Gui.buttonWidthSpace,
            () => {
                SimpleCameraInput.turn(
                    cameras.freeCamera,
                    false,
                    guiMoveMultiplier,
                )
            },
        )

        this.addDebugButton("Start Maze", 0, () => {
            stages.startMaze()
        })

        this.addDebugButton("Non-User Example", 1, () => {
            stages.startExample1()
        })

        this.addDebugButton("User Example", 2, () => {
            MazeStore.instance.mazeCigs = MazeCigs.forMazeRunner()
            stages.startExample2()
        })

        this.addDebugButton("User Maze Preamble", 3, () => {
            stages.userMazePreamble()
        })

        this.addDebugButton("Attempt the Maze", 4, () => {
            stages.startUserMaze(true)
        })

        this.addDebugButton("Maze Done", 5, () => {
            stages.mazeOver()
        })

        this.addDebugButton("Maze Top View", 6, () => {
            stages.realMazeTopView()
        })
    }

    disposeTeen = () => {
        if (this.teenTitle) {
            this.teenTitle.dispose()
        }
        if (this.pawnImage) {
            this.pawnImage.dispose()
        }
        if (this.pawnText) {
            this.pawnText.dispose()
        }
    }

    addTeen = (smoker?: boolean) => {
        this.disposeTeen()
        this.teenTitle = this.addGuiText("Teen", {
            top: guiScreenPositions.second,
            fontSize: Gui.titleFontSize,
            fontType: FontType.HEADER,
        })
        if (smoker != null) {
            let text
            let pawn
            if (smoker) {
                text = "Tried\nTobacco"
                pawn = mazeImages.smoker
            } else {
                text = "Non-User"
                pawn = mazeImages.nonSmoker
            }
            const pawnImage = new Image("pawn", pawn)
            pawnImage.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT
            pawnImage.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP
            pawnImage.left = this.guiLeft
            pawnImage.top = guiScreenPositions.belowSecond
            pawnImage.width = `${75 * Gui.windowScaling}px`
            pawnImage.height = `${75 * Gui.windowScaling}px`
            this.pawnImage = pawnImage

            const pawnTextTop = smoker ? 0 : Gui.padding * 0.9
            this.pawnText = this.addGuiText(text, {
                top: guiScreenPositions.belowSecond + pawnTextTop,
                left: this.guiLeft + Gui.padding * 3.1,
                fontSize: 30,
            })

            this.gui.addControl(pawnImage)
        }
    }

    createTimer = (seconds?: number) => {
        if (!this.timerTitle) {
            this.timerTitle = this.addGuiText("Timer", {
                top: guiScreenPositions.first,
                fontSize: Gui.titleFontSize,
                fontType: FontType.HEADER,
            })
        }
        this.timerSeconds = seconds
        if (this.timerText) {
            this.timerText.dispose()
        }
        if (seconds != null) {
            this.timerText = this.addGuiText(
                TimeUtils.convertSecondsToTimer(seconds),
                {
                    top: guiScreenPositions.belowFirst,
                    fontSize: Gui.titleFontSize,
                },
            )
        }
    }

    startTimer = () => {
        const { clock, sfxVolume } = MazeAudio.instance
        clock.setVolume(sfxVolume)
        clock.loop = true
        clock.play()
        this.timerInterval = setInterval(() => {
            if (!document.hidden) {
                this.timerSeconds--
                this.timerText.text = TimeUtils.convertSecondsToTimer(
                    this.timerSeconds,
                )
                if (this.timerSeconds < 1 && this.onTimerComplete) {
                    this.onTimerComplete()
                }
            }
        }, 1000)
    }

    stopTimer = () => {
        MazeAudio.instance.clock.stop()
        clearInterval(this.timerInterval)
    }

    disposeTimer = () => {
        this.timerTitle?.dispose()
        this.timerText?.dispose()
        clearInterval(this.timerInterval)
    }

    addGuiText = (
        text: string,
        {
            top = guiScreenPositions.mid,
            left = this.guiLeft,
            fontSize = 22,
            fontType = FontType.BODY,
            align = true,
            parentContainer,
        }: {
            top?: number
            left?: number
            fontSize?: number
            fontType?: FontType
            align?: boolean
            parentContainer?: Container
        } = {},
    ) => {
        const textBlock = new TextBlock()
        textBlock.text = text
        textBlock.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT
        if (align) {
            textBlock.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT
            textBlock.textVerticalAlignment = Control.VERTICAL_ALIGNMENT_TOP
            textBlock.top = top
            textBlock.left = left
        }
        textBlock.fontSize = fontSize * Gui.windowScaling
        textBlock.fontFamily = fontType
        textBlock.textWrapping = true
        textBlock.width = this.maxWidth()
        textBlock.color = Gui.color

        const container = parentContainer ? parentContainer : this.gui
        container.addControl(textBlock)
        this.textBlock = textBlock
        return textBlock
    }

    removeGuiText = () => (this.textBlock ? this.textBlock.dispose() : null)

    addTextBullet = (
        bulletText: string,
        startPosition = guiScreenPositions.second,
    ) => {
        this.currentBulletText = `${this.currentBulletText}${
            this.currentBulletText.length > 0 ? "\n\n" : ""
        }• ${bulletText}`
        this.removeGuiText()
        this.addGuiText(this.currentBulletText, {
            top: startPosition,
            fontSize: 18,
        })
    }

    createBulletBuilder = (startPosition?: number) => {
        const timer = BabTimer.create()
        return {
            addBullet: (time: number, bulletText: string) => {
                timer.add(time, () =>
                    this.addTextBullet(bulletText, startPosition),
                )
            },
            start: () => timer.start(),
        }
    }

    removeBullets = () => {
        this.removeGuiText()
        this.currentBulletText = ""
    }

    stopSmokeTimer = () => {
        if (this.textBlock != null) {
            this.textBlock.dispose()
        }
        if (this.smokerTimerInterval != null) {
            clearInterval(this.smokerTimerInterval)
        }

        if (this.timerImages != null) {
            this.timerImages.forEach((timer) => timer.dispose())
            this.timerImages = undefined
        }
        if (this.timerCig != null) {
            this.timerCig.dispose()
            this.timerCig = undefined
        }
    }

    startSmokeTimer = (complete: () => void, ecig: boolean) => {
        const intervalDuration = 1000
        let secondsSpent = 0

        if (ecig) {
            this.timerCig = new Image("timerEcig", mazeImages.ecig)
            this.timerCig.width = "45px"
            this.timerCig.height = "8px"
        } else {
            this.timerCig = new Image("timerCig", mazeImages.cig)
            this.timerCig.width = "42px"
            this.timerCig.height = "39px"
        }
        this.gui.addControl(this.timerCig)

        mazeImages.timers.forEach((imageRef, idx) => {
            const timerImage = new Image(`timerImage${idx}`, imageRef)
            timerImage.width = "200px"
            timerImage.height = "283px"
            this.gui.addControl(timerImage)
            this.timerImages.push(timerImage)
        })

        this.smokerTimerInterval = setInterval(() => {
            secondsSpent++

            this.timerImages.shift().dispose()

            if (secondsSpent > 7 || this.timerSeconds < 1) {
                clearInterval(this.smokerTimerInterval)
                this.smokerTimerInterval = undefined

                this.timerCig.dispose()
                this.timerCig = undefined
                complete()
            }
        }, intervalDuration)
    }

    maxWidth = () =>
        `${MazeStore.instance.width * Gui.percentWidth - Gui.padding * 2}px`

    addNextButton = (onClick: () => void, name = "Next", top?: number) => {
        const next = this.addGuiButton(name, {
            onClick: () => {
                next.dispose()
                onClick()
            },
            top,
        })
        this.gui.addControl(next)
    }

    addGuiButton = (
        name: string,
        {
            top = guiScreenPositions.bottom(),
            align = true,
            onClick,
        }: { top?: number; align?: boolean; onClick?: () => void } = {},
    ) => {
        const button = Button.CreateSimpleButton(`but${name}`, name)
        button.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT
        if (align) {
            button.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP
            button.left = this.guiLeft
            button.top = top
        }

        button.width = "100px"
        button.height = "50px"
        button.color = "white"

        if (onClick) {
            button.onPointerUpObservable.add(onClick)
        }

        return button
    }

    addRadioOptions = (
        description: string,
        options: Array<{ name: string; result: () => void }>,
        descBlockHeight = 180,
        startPosition = guiScreenPositions.aboveMid,
    ) => {
        const container = new StackPanel()
        this.gui.addControl(container)
        container.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP
        container.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT
        container.left = this.guiLeft
        container.top = startPosition
        const descBlock = this.addGuiText(description, {
            align: false,
            parentContainer: container,
        })
        descBlock.height = `${descBlockHeight * Gui.windowScaling}px`

        const next = this.addGuiButton("Next", { align: false })
        next.isEnabled = false
        next.paddingTop = "10px"
        options.forEach((option) => {
            const button = new RadioButton()
            button.width = "20px"
            button.height = "20px"
            button.group = description
            button.color = "white"
            button.background = "black"
            const header = Control.AddHeader(button, option.name, "100px", {
                isHorizontal: true,
                controlFirst: true,
            })
            header.height = "40px"
            header.color = "white"
            header.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT

            button.onIsCheckedChangedObservable.add((isSelected) => {
                if (isSelected) {
                    next.onPointerUpObservable.clear()
                    next.onPointerUpObservable.addOnce(() => {
                        option.result()
                        container.dispose()
                    })
                }
            })

            container.addControl(header)
        })

        container.addControl(next)
        return next
    }

    setShowMoveButtons = (show = true) => {
        this.moveButtons.forEach((button) => (button.isVisible = show))
        this.showMoveButtons = show
    }

    addDebugButton = (name: string, order: number, action: () => void) => {
        if (!CCUtils.debug) {
            return
        }
        const button = Button.CreateSimpleButton(`but${name}`, name)
        button.width = "100px"
        button.height = "50px"
        button.top = -200 + order * 60
        button.color = "white"

        button.onPointerUpObservable.add(() => {
            action()
            this.debugButtons.forEach((debugButton) => debugButton.dispose())
        })

        this.gui.addControl(button)
        this.debugButtons.push(button)
    }

    addMoveButton = (
        name: string,
        x: number,
        y: number,
        action: () => void,
    ) => {
        const button = Button.CreateImageOnlyButton(
            "but" + name,
            mazeImages[`${name}Button`],
        )
        button.verticalAlignment = Control.VERTICAL_ALIGNMENT_BOTTOM
        button.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER
        button.width = `${Gui.buttonSide}px`
        button.height = `${Gui.buttonSide}px`
        button.left = x - Gui.buttonWidthSpace - Gui.buttonSpacing
        button.top = y

        if (!this.showMoveButtons) {
            button.isVisible = false
        }

        button.onPointerDownObservable.add(() => {
            this.clearMove()
            action()
            this.moveInterval = setInterval(action, 50)
        })
        button.onPointerUpObservable.add(this.clearMove)
        this.gui.addControl(button)
        this.moveButtons.push(button)

        return button
    }

    clearMove = () => {
        if (this.moveInterval) {
            clearInterval(this.moveInterval)
            this.moveInterval = undefined
        }
    }

    enableMoveButtons = (enabled = true) => {
        this.moveButtons.forEach((button) => (button.isEnabled = enabled))
        this.clearMove()
    }

    fullScreenButton = (text: string, action: () => void) => {
        const { width, height } = MazeStore.instance
        const button = Button.CreateImageWithCenterTextButton(
            "Full screen",
            text,
            mazeImages.transparentPixel,
        )
        button.width = `${width}px`
        button.height = `${height}px`

        button.color = "#FFFFFF"
        button.fontSizeInPixels = 25
        button.onPointerUpObservable.addOnce(() => {
            action()
            button.dispose()
        })
        this.gui.addControl(button)
    }
}

export const guiScreenPositions = {
    first: Gui.padding,
    belowFirst: Gui.padding * 3,
    second: Gui.padding * 6,
    belowSecond: Gui.padding * 9,
    aboveMid: Gui.padding * 12,
    mid: Gui.padding * 15,
    bottom: () => MazeStore.instance.height - 70,
}
